import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { listMessages } from "~/entities/chat";
import type { MessageListResponse } from "~/entities/chat";

export const IN_CHAT_SEARCH_MIN_CHARS = 3;
export const IN_CHAT_SEARCH_DEBOUNCE_MS = 250;
const IN_CHAT_SEARCH_LIMIT = 50;

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
}

export const inChatSearchKey = (chatId: string, query: string) =>
    ["v2", "chats", chatId, "messages", "search", query] as const;

export function useInChatSearch(query: string, chatId: string | null | undefined) {
    const trimmed = query.trim();
    const debounced = useDebouncedValue(trimmed, IN_CHAT_SEARCH_DEBOUNCE_MS);
    const enabled = !!chatId && debounced.length >= IN_CHAT_SEARCH_MIN_CHARS;

    return useQuery<MessageListResponse>({
        queryKey: inChatSearchKey(chatId ?? "", debounced),
        queryFn: () =>
            listMessages(chatId!, {
                limit: IN_CHAT_SEARCH_LIMIT,
                search: debounced,
                order: "desc",
            }),
        enabled,
        staleTime: 10_000,
        placeholderData: keepPreviousData,
    });
}
